import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function SuccessPage() {
    const [status, setStatus] = useState('checking');
    const [booking, setBooking] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    // ✅ Тип оплаты из URL (?type=booking | subscription)
    const paymentType = (() => {
        try {
            const p = new URLSearchParams(window.location.search);
            return p.get('type'); // 'booking' | 'subscription' | null
        } catch { return null; }
    })();

    const updateUserData = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const profileRes = await fetch(API_URL + '/profile', {
                headers: { 'Authorization': 'Bearer ' + token }
            });
            if (profileRes.ok) {
                const userData = await profileRes.json();
                localStorage.setItem('user', JSON.stringify(userData));
                setCurrentUserId(userData.id);
            }

            const subRes = await fetch(API_URL + '/my-subscription', {
                headers: { 'Authorization': 'Bearer ' + token }
            });
            if (subRes.ok) {
                const subData = await subRes.json();
                setSubscription(subData.is_active ? subData : null);
            }

            window.dispatchEvent(new CustomEvent('userDataUpdated'));
        } catch (error) {
            console.error('Ошибка обновления данных:', error);
        }
    };

    useEffect(() => {
        const checkStatus = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setStatus('unknown');
                    return;
                }

                // Загружаем пользователя (нужен id для определения роли)
                const profileRes = await fetch(API_URL + '/profile', {
                    headers: { 'Authorization': 'Bearer ' + token }
                });
                if (profileRes.ok) {
                    const me = await profileRes.json();
                    setCurrentUserId(me.id);
                }

                // ✅ Если ?type=subscription → сразу проверяем подписку
                if (paymentType === 'subscription') {
                    const subRes = await fetch(API_URL + '/my-subscription', {
                        headers: { 'Authorization': 'Bearer ' + token }
                    });
                    if (subRes.ok) {
                        const subData = await subRes.json();
                        if (subData.is_active) {
                            setSubscription(subData);
                            setStatus('subscription_paid');
                            await updateUserData();
                            return;
                        }
                    }
                    setStatus('unknown');
                    return;
                }

                // ✅ Проверяем последнюю бронь
                const bookingRes = await fetch(API_URL + '/bookings/last', {
                    headers: { 'Authorization': 'Bearer ' + token }
                });

                if (bookingRes.ok) {
                    const data = await bookingRes.json();
                    setBooking(data);

                    if (data.payment_status === 'paid') {
                        setStatus('booking_paid');
                        await updateUserData();
                        return;
                    }
                    if (data.payment_status === 'held') {
                        setStatus('booking_held');
                        return;
                    }
                    if (data.payment_status === 'cancelled') {
                        setStatus('booking_cancelled');
                        return;
                    }
                    if (data.payment_status === 'unpaid') {
                        const age = Date.now() - new Date(data.createdAt).getTime();
                        if (age < 30 * 60 * 1000) {
                            setStatus('booking_unpaid');
                            return;
                        }
                    }
                }

                // ✅ Если ?type=booking, но брони не нашли → unknown
                if (paymentType === 'booking') {
                    setStatus('unknown');
                    return;
                }

                // ✅ Иначе (без ?type) — fallback: проверяем подписку
                const subRes = await fetch(API_URL + '/my-subscription', {
                    headers: { 'Authorization': 'Bearer ' + token }
                });
                if (subRes.ok) {
                    const subData = await subRes.json();
                    if (subData.is_active) {
                        setSubscription(subData);
                        setStatus('subscription_paid');
                        await updateUserData();
                        return;
                    }
                }

                setStatus('unknown');
            } catch (error) {
                console.error('Ошибка проверки:', error);
                setStatus('unknown');
            }
        };

        checkStatus();
        const timer = setTimeout(checkStatus, 2500);
        return () => clearTimeout(timer);
    }, []);

    // Определяем роль: заказчик или исполнитель
    const isCustomer = booking && currentUserId && String(booking.customer_id) === String(currentUserId);
    const isExecutor = booking && currentUserId && String(booking.owner_id) === String(currentUserId);

    // ========== ПРОВЕРКА ==========
    if (status === 'checking') {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.icon}>⏳</div>
                    <h1 style={styles.title}>Проверяем статус оплаты...</h1>
                    <p style={styles.text}>Подождите несколько секунд</p>
                </div>
            </div>
        );
    }

    // ========== БРОНЬ ОПЛАЧЕНА (деньги заморожены) ==========
    if (status === 'booking_paid' || status === 'booking_held') {
        return (
            <div style={{ ...styles.container, backgroundColor: '#f0fdf4' }}>
                <div style={styles.card}>
                    <div style={styles.icon}>✅</div>
                    <h1 style={{ ...styles.title, color: '#166534' }}>
                        {isExecutor ? 'Заявка принята!' : 'Оплата прошла успешно!'}
                    </h1>
                    <p style={styles.text}>
                        Бронирование <strong>#{booking?.id}</strong>
                        <br/>
                        {isExecutor ? (
                            <>
                                Заказчик оплатил <strong>{Number(booking?.payout || 0).toLocaleString('ru-RU')} ₽</strong> — ваша сумма.
                                <br/>
                                Подтвердите выполнение работ после завершения.
                            </>
                        ) : (
                            <>
                                Деньги <strong>{Number(booking?.total_price || 0).toLocaleString('ru-RU')} ₽</strong> заморожены
                                и будут переведены исполнителю после подтверждения работ.
                            </>
                        )}
                    </p>

                    <div style={styles.infoBox}>
                        <div style={styles.infoTitle}>Что дальше?</div>
                        {isExecutor ? (
                            <ul style={styles.list}>
                                <li>Свяжитесь с заказчиком по телефону</li>
                                <li>Выполните работы в срок</li>
                                <li>Нажмите «Подтвердить выполнение» в разделе «Сделки»</li>
                            </ul>
                        ) : (
                            <ul style={styles.list}>
                                <li>Исполнитель свяжется с вами</li>
                                <li>После выполнения работ нажмите «Работа выполнена» в разделе «Сделки»</li>
                                <li>Деньги переведутся исполнителю</li>
                            </ul>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <a href="/" style={styles.button}>🏠 На главную</a>
                        <a href="/?view=myBookings" style={styles.buttonSecondary}>📋 К сделкам</a>
                    </div>
                </div>
            </div>
        );
    }

    // ========== ТАРИФ ОПЛАЧЕН ==========
    if (status === 'subscription_paid') {
        return (
            <div style={{ ...styles.container, backgroundColor: '#f0fdf4' }}>
                <div style={styles.card}>
                    <div style={styles.icon}>✅</div>
                    <h1 style={{ ...styles.title, color: '#166534' }}>
                        Тариф активирован!
                    </h1>
                    <p style={styles.text}>
                        Тариф <strong>"{subscription?.tariff?.name}"</strong> успешно активирован.
                        <br/>
                        Теперь вы можете размещать объявления и откликаться на заявки.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <a href="/" style={styles.button}>🏠 На главную</a>
                        <a href="/?view=list" style={styles.buttonSecondary}>📋 К заявкам</a>
                    </div>
                </div>
            </div>
        );
    }

    // ========== БРОНЬ НЕ ОПЛАЧЕНА ==========
    if (status === 'booking_unpaid') {
        return (
            <div style={{ ...styles.container, backgroundColor: '#fffbeb' }}>
                <div style={styles.card}>
                    <div style={styles.icon}>⚠️</div>
                    <h1 style={{ ...styles.title, color: '#92400e' }}>
                        Оплата не завершена
                    </h1>
                    <p style={styles.text}>
                        Бронирование <strong>#{booking?.id}</strong> создано, но ещё не оплачено.
                        <br/>
                        Вы можете оплатить его в разделе «Сделки».
                    </p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <a href="/?view=myBookings" style={styles.button}>💳 К сделкам</a>
                        <a href="/" style={styles.buttonSecondary}>🏠 На главную</a>
                    </div>
                </div>
            </div>
        );
    }

    // ========== БРОНЬ ОТМЕНЕНА ==========
    if (status === 'booking_cancelled') {
        return (
            <div style={{ ...styles.container, backgroundColor: '#fef2f2' }}>
                <div style={styles.card}>
                    <div style={styles.icon}>❌</div>
                    <h1 style={{ ...styles.title, color: '#991b1b' }}>
                        Оплата отменена
                    </h1>
                    <p style={styles.text}>
                        Бронирование <strong>#{booking?.id}</strong> было отменено.
                        <br/>
                        Деньги не списаны. Вы можете создать новую бронь.
                    </p>
                    <a href="/" style={styles.button}>🏠 Вернуться на главную</a>
                </div>
            </div>
        );
    }

    // ========== НЕИЗВЕСТНО ==========
    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.icon}>🤔</div>
                <h1 style={styles.title}>Проверьте статус заказа</h1>
                <p style={styles.text}>
                    Не удалось автоматически определить статус оплаты.
                    Откройте раздел «Сделки» — там отображается актуальный статус.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <a href="/?view=myBookings" style={styles.button}>📋 К сделкам</a>
                    <a href="/" style={styles.buttonSecondary}>🏠 На главную</a>
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f0fdf4',
        padding: '20px'
    },
    card: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '48px 32px',
        maxWidth: '560px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
    },
    icon: {
        fontSize: '72px',
        marginBottom: '16px'
    },
    title: {
        fontSize: '28px',
        fontWeight: 700,
        marginBottom: '16px',
        color: '#166534'
    },
    text: {
        fontSize: '16px',
        color: '#64748b',
        marginBottom: '24px',
        lineHeight: 1.6
    },
    infoBox: {
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        textAlign: 'left'
    },
    infoTitle: {
        fontWeight: 700,
        fontSize: '14px',
        color: '#0f172a',
        marginBottom: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    list: {
        margin: 0,
        paddingLeft: '20px',
        color: '#475569',
        fontSize: '14px',
        lineHeight: 1.8
    },
    button: {
        display: 'inline-block',
        backgroundColor: '#16a34a',
        color: 'white',
        padding: '14px 28px',
        borderRadius: '10px',
        textDecoration: 'none',
        fontSize: '15px',
        fontWeight: 600
    },
    buttonSecondary: {
        display: 'inline-block',
        backgroundColor: '#f1f5f9',
        color: '#0f172a',
        padding: '14px 28px',
        borderRadius: '10px',
        textDecoration: 'none',
        fontSize: '15px',
        fontWeight: 600
    }
};

export default SuccessPage;
